/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import {
  Ago,
  BigText,
  DATE_NATIVE_FORMAT,
  formatDate,
  formatValidDate,
  parseDateFromString,
  PortalCard,
  ConfirmModal,
  ConnectButton,
  SocialProvider,
} from "@advapacs/common";
import { IRootState } from "@src/config/reducers";
import { useAppDispatch } from "@src/config/store";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import GiftIcon from "@src/assets/icons/gifts.svg";
import CallIcon from "@src/assets/icons/call.svg";
import MailIcon from "@src/assets/icons/mail.svg";
import GenderIcon from "@src/assets/icons/gender.svg";
import UserCircle from "@src/assets/icons/person-circle-2-icon.svg";
import { getSessions } from "./ducks/operations";
import axios from "axios";
import { NoResult } from "@src/components/NoResult";
import { sexOptions } from "@src/constants";
import { localisationSelector } from "@src/pages/unauthenticated/ducks/selectors";
import { ISessionsResponse } from "./ducks/slices";
import { getPatientName, openWindowOpenOauthURL } from "@src/utils/utils";
import { PPIcon } from "@src/components/PPIcon";
import { ISocialResponse, SocialItem } from "./SocialItem";

const AccountContainer: FC = () => {
  const dispatch = useAppDispatch();
  const [socialAccount, setSocialAccount] = useState<ISocialResponse[]>([]);
  const [selectedSocialAccount, setSelectedSocialAccount] =
    useState<ISocialResponse>(null);
  const { isMobile, isMobileOnly, isTablet } = useSelector(
    (state: IRootState) => state.responsiveReducer
  );
  const localisation = useSelector(localisationSelector);

  const { account } = useSelector(
    (state: IRootState) => state.unAuthenticatedReducer
  );
  const { sessions } = useSelector((state: IRootState) => state.accountReducer);
  const [showRemoveAllSessionsModal, setShowRemoveAllSessionsModal] =
    useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [showRemoveOneSessionsModal, setShowRemoveOneSessionsModal] =
    useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [showDisconnectAllModal, setShowDisconnectAllModal] = useState(false);

  const handleFetchingSocialLogin = async () => {
    try {
      const rs = await axios.get("/externalIdentities");
      setSocialAccount(rs.data?.content);
    } catch {}
  };

  const handleDeleteAllSessions = async () => {
    if (sessions.length === 0) return;
    try {
      await axios.delete("/authenticatedPatient/sessions");
    } catch (error) {
    } finally {
      dispatch(getSessions());
    }
  };

  const handleDeleteSession = async (sId?: string) => {
    try {
      await axios.delete("/authenticatedPatient/sessions/" + sId);
    } catch (error) {
    } finally {
      dispatch(getSessions());
      if (account.id === sId) {
        localStorage.removeItem("remember"); // should remove our session local storage with key remember me.
      }
    }
  };

  const dobFormat = useMemo(() => {
    return account?.dob
      ? formatDate(
          parseDateFromString(account?.dob, DATE_NATIVE_FORMAT),
          localisation?.dateFormat
        )
      : null;
  }, [account, localisation]);

  const SessionItem = (s: ISessionsResponse) => {
    return (
      <PortalCard
        key={s.id}
        isMobile={isMobile || isTablet}
        isMobileOnly={isMobile || isTablet}
        wrapperClass="study-card m-t-md"
      >
        <div className="">
          <h5 className="font-weight">{s.device}</h5>

          {isMobile ||
            (isTablet && (
              <>
                <div className="m-t-xs d-flex ">
                  <span className="text-label-fixed account m-r-xs">
                    Last Accessed
                  </span>
                  <Ago utc={s.createdAt} />
                </div>

                <div className="m-t-xs d-flex ">
                  <span className="text-label-fixed account m-r-xs">
                    Expires At
                  </span>
                  <span>
                    {formatValidDate(s.expiresAt, localisation?.dateTimeFormat)}
                  </span>
                </div>

                <div className="m-t-xs d-flex ">
                  <span className="text-label-fixed account m-r-xs">
                    Location
                  </span>
                  <BigText text={s.location} />
                </div>

                <div className="m-t-xs d-flex ">
                  <span className="text-label-fixed account m-r-xs">
                    IP Address
                  </span>
                  <BigText text={s.ip} />
                </div>

                {!s.current && (
                  <button
                    className="m-t-xs w-100 btn btn-light border-danger text-danger bg-white"
                    onClick={() => {
                      setSelectedSessionId(s.id);
                      setShowRemoveOneSessionsModal(true);
                    }}
                  >
                    Sign Out
                  </button>
                )}
              </>
            ))}

          {!isMobile && !isTablet && (
            <div className="flexbox align-items-center w-100">
              <div className="m-t-xs  flex-1">
                <div className="flexbox">
                  <span className="m-r-xs text-label-fixed account">
                    Last Accessed
                  </span>
                  <Ago utc={s.createdAt} />
                </div>

                <div className="flexbox">
                  <span className="m-r-xs text-label-fixed account">
                    Expires At
                  </span>
                  <BigText
                    maxWidth={"100%"}
                    text={formatValidDate(
                      s.expiresAt,
                      localisation?.dateTimeFormat
                    )}
                  />
                </div>
              </div>
              <div className="m-t-xs  flex-1">
                <div className="flexbox">
                  <span className="m-r-xs text-label-fixed account">
                    Location
                  </span>
                  <BigText text={s.location} />
                </div>

                <div className="flexbox">
                  <span className="m-r-xs text-label-fixed account">
                    IP Address
                  </span>
                  <BigText text={s.ip} />
                </div>
              </div>
              {!s.current && (
                <button
                  className="m-t-xs btn btn-light border-danger text-danger w-100px bg-white"
                  onClick={() => {
                    setSelectedSessionId(s.id);
                    setShowRemoveOneSessionsModal(true);
                  }}
                >
                  Sign Out
                </button>
              )}
            </div>
          )}
        </div>
      </PortalCard>
    );
  };
  const emailMaxWidth = useMemo(() => {
    if (isMobileOnly || isMobile) return 350;

    if (isTablet) return 768;

  }, [isMobile, isMobileOnly, isTablet]);

  const handleDisconnectSocialAccount = async () => {
    try {
      await axios.delete("/externalIdentities/" + selectedSocialAccount?.id);
    } catch (error) {
    } finally {
      setShowDisconnectModal(false);
      setSelectedSocialAccount(null);
      handleFetchingSocialLogin();
    }
  };

  const handleDisconnectAllSocialAccount = async () => {
    try {
      // show modal confirm disconnect all account
      await axios.delete("/externalIdentities");
    } catch (error) {
    } finally {
      setShowDisconnectAllModal(false);
      handleFetchingSocialLogin();
    }
  };

  const handleLoginWith = async (type: string) => {
    try {
      const rs = await axios.get(`/externalIdentities/connect/${type}`);

      // open popup
      openWindowOpenOauthURL(rs?.data?.redirect);
    } catch {}
  };

  useEffect(() => {
    dispatch(getSessions());
    handleFetchingSocialLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {showRemoveAllSessionsModal && (
        <ConfirmModal
          body="Are you sure you want to remove all trusted browsers?"
          confirmBtnName="Sign Out All"
          cancelBtnName="Cancel"
          cancel={() => {
            setShowRemoveAllSessionsModal(false);
          }}
          confirm={() => {
            handleDeleteAllSessions();
            setShowRemoveAllSessionsModal(false);
          }}
          show={showRemoveAllSessionsModal}
        />
      )}

      {showRemoveOneSessionsModal && (
        <ConfirmModal
          body="Are you sure you want to remove this trusted browsers?"
          confirmBtnName="Remove"
          cancelBtnName="Cancel"
          cancel={() => {
            setShowRemoveOneSessionsModal(false);
            setSelectedSessionId("");
          }}
          confirm={() => {
            handleDeleteSession(selectedSessionId);
            setShowRemoveOneSessionsModal(false);
            setSelectedSessionId("");
          }}
          show={showRemoveOneSessionsModal}
        />
      )}

      {showDisconnectModal && (
        <ConfirmModal
          body="Are you sure you want to disconnect the social account?"
          confirmBtnName="Disconnect"
          cancelBtnName="Cancel"
          cancel={() => {
            setShowDisconnectModal(false);
            setSelectedSocialAccount(null);
          }}
          confirm={() => {
            handleDisconnectSocialAccount();
          }}
          show={showDisconnectModal}
        />
      )}

      {showDisconnectAllModal && (
        <ConfirmModal
          body="Are you sure you want to disconnect all the social accounts?"
          confirmBtnName="Disconnect All"
          cancelBtnName="Cancel"
          cancel={() => {
            setShowDisconnectAllModal(false);
          }}
          confirm={() => {
            handleDisconnectAllSocialAccount();
          }}
          show={showDisconnectAllModal}
        />
      )}

      <div>
        <PortalCard
          isMobile={isMobile}
          isMobileOnly={isMobileOnly}
          wrapperClass="study-card border-none box-shadow-none p-0 m-b-md"
        >
          <div className="flexbox flex-center-between">
            <h4 className="font-weight m-b-sm">My Profile</h4>
          </div>
          <div className="d-flex align-items-start">
            <img className="m-r-xs" src={UserCircle} alt="Gift Icon" />
            <div className="title flex-1">
              <h4 className="font-weight">{getPatientName(account)}</h4>
              <p className="pid">
                <span>{account?.pid}</span>
              </p>
              <p className="pid d-flex align-items-center">
                <PPIcon src={GiftIcon} alt="Gift Icon" className="m-r-xs" />
                <span>{dobFormat}</span>
              </p>
              <p className="pid d-flex align-items-center">
                <PPIcon
                  src={GenderIcon}
                  alt="Gender Icon"
                  width={20}
                  height={20}
                  className="m-r-xs"
                />

                <span>
                  {sexOptions.find((x) => x.value === account?.sex)?.label}
                </span>
              </p>

              <p className="pid d-flex align-items-center">
                <PPIcon src={CallIcon} alt="Call Icon" className="m-r-xs" />
                <span>{account?.mobilePhone}</span>
              </p>

              <div
                className="pid d-flex align-items-center"
                style={{
                  maxWidth:
                    isMobile || isTablet ? emailMaxWidth : "calc(100% - 100px)",
                }}
              >
                <PPIcon src={MailIcon} alt="Mail Icon" className="m-r-xs" />
                <BigText maxWidth={"100%"} text={account?.email} />
              </div>
            </div>
          </div>
        </PortalCard>

        <div className="m-b-md">
          <div
            className={`sessions-title m-b-md d-flex align-items-center justify-content-between`}
          >
            <h4 className="font-weight ">
              Social Logins ({socialAccount.length})
            </h4>

            <span className="d-flex flex-center">
              {!isMobile && !isTablet && (
                <>
                  <div className="flexbox">
                    <ConnectButton
                      type={SocialProvider.FACEBOOK}
                      className="ml-2"
                      onConnect={() => handleLoginWith(SocialProvider.FACEBOOK)}
                    />
                    <ConnectButton
                      type={SocialProvider.GOOGLE}
                      className="ml-2"
                      onConnect={() => handleLoginWith(SocialProvider.GOOGLE)}
                    />
                    <ConnectButton
                      type={SocialProvider.APPLE}
                      className="ml-2"
                      onConnect={() => handleLoginWith(SocialProvider.APPLE)}
                    />
                  </div>
                </>
              )}

              <span
                className={`text-danger cursor-${
                  socialAccount.length > 0 ? "pointer" : "not-allowed"
                } m-l-xs font-weight`}
                onClick={async () => {
                  if (socialAccount?.length > 0) {
                    setShowDisconnectAllModal(true);
                  }
                }}
              >
                Disconnect All
              </span>
            </span>
          </div>

          {(isMobile || isTablet) && (
            <>
              <ConnectButton
                className="d-block w-100 m-b-xs"
                type={SocialProvider.GOOGLE}
                onConnect={() => handleLoginWith(SocialProvider.GOOGLE)}
              />

              <ConnectButton
                className="d-block w-100 m-b-xs"
                type={SocialProvider.FACEBOOK}
                onConnect={() => handleLoginWith(SocialProvider.FACEBOOK)}
              />
              <ConnectButton
                className="d-block w-100 m-b-xs"
                type={SocialProvider.APPLE}
                onConnect={() => handleLoginWith(SocialProvider.APPLE)}
              />
            </>
          )}

          {socialAccount.map((sc) => {
            return (
              <SocialItem
                isMobile={isMobile || isTablet}
                isMobileOnly={isMobile || isTablet}
                dateTimeFormat={localisation?.dateTimeFormat}
                item={sc}
                onDisconnect={async () => {
                  // show modal confirm disconnect each account
                  setSelectedSocialAccount(sc);
                  setShowDisconnectModal(true);
                }}
              />
            );
          })}

          {socialAccount.length === 0 && <NoResult type="Accounts" />}
        </div>

        <div
          className={`sessions-title m-b-md d-flex align-items-center justify-content-between`}
        >
          <h4 className="font-weight ">
            Other Active Sessions ({sessions.length})
          </h4>
          <span
            className={`text-danger cursor-${
              sessions.length > 0 ? "pointer" : "not-allowed"
            } m-l-xs font-weight`}
            onClick={() => {
              if (sessions?.length > 0) setShowRemoveAllSessionsModal(true);
            }}
          >
            Sign Out All
          </span>
        </div>

        {sessions
          .filter((x) => x.current)
          .map((s) => {
            return SessionItem(s);
          })}

        {sessions
          .filter((x) => !x.current)
          .map((s) => {
            return SessionItem(s);
          })}

        {sessions.length === 0 && <NoResult type="Sessions" />}
      </div>
    </>
  );
};

export default AccountContainer;
